// Correction locale d'une séance d'active recall : aucun appel réseau, aucune IA, réponse immédiate.
//
// CE QU'IL FAIT. Pour chaque point numéroté de la fiche, il dit si tes notes le restituent — le même cochage
// ok / partiel / manque que la relecture IA, dans le même format, donc tout l'écran de bilan fonctionne sans
// changer. Il s'appuie sur src/domain/qcm/lexique.js : « HTA » vaut « hypertension artérielle », « ttt » vaut
// « traitement », « 7 mmol/L » vaut « 1,26 g/L », « 48 h » vaut « 2 jours ».
//
// CE QU'IL NE FAIT PAS, et il vaut mieux le dire que le laisser croire : il ne comprend pas une phrase. Il
// reconnaît des concepts et des valeurs, pas un raisonnement. Il ne détecte donc qu'une seule sorte d'erreur —
// la contradiction chiffrée, où tu donnes un nombre à la place d'un autre — parce que c'est la seule qu'on
// puisse établir sans comprendre. Confondre deux entités, inverser une conduite à tenir : ça, il ne le voit
// pas, et il ne le prétend pas. C'est un premier passage gratuit et instantané, pas un remplacement de l'IA.
import { GROUPES, VIDES, UNITES, EQUIV_VALEURS } from './lexique.js';

// ----------------------------------------------------------------- normalisation
function sansAccents(s) {
    return String(s == null ? '' : s).normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[’‘‛]/g, "'").replace(/[œŒ]/g, 'oe').replace(/[æÆ]/g, 'ae').toLowerCase();
}
// Découpe en jetons. La barre oblique est GARDÉE : elle porte du sens dans « mmol/l » comme dans « 140/90 ».
function jetons(texte) {
    return sansAccents(texte)
        .replace(/[≥≤<>=~±]/g, ' ')
        .replace(/(\d)\s+(\d{3})\b/g, '$1$2')          // « 1 000 » est un seul nombre
        .replace(/\b([ldnjmtscq]|qu|jusqu|lorsqu)'/g, ' ')  // élision : « l'échographie » cachait « ETT »
        .replace(/[^a-z0-9%/.,'-]+/g, ' ')
        .replace(/(^|\s)[-',.]+|[-',.]+(?=\s|$)/g, ' ')
        .split(/\s+/).filter(Boolean)
        // « 10j », « 48h », « 7% », « 1,26g/l » : le nombre et son unité sont collés dans des notes prises vite.
        .map(function(j) { return j.replace(/^[\/x*]+(?=\d)/, ''); })   // « /6 mois », « x3 »
        .reduce(function(acc, j) {
            var m = /^(\d+(?:[.,]\d+)?(?:[-\/]\d+(?:[.,]\d+)?)?)(%|[a-z]+(?:\/[a-z0-9]+)*)$/.exec(j);
            // Unité connue, pourcentage, ou unité composée d'une posologie : le nombre se détache dans tous
            // les cas — faute de quoi « 1mg/kg/j » ne donne aucune valeur du tout.
            if (m && (m[2] === '%' || uniteDe(m[2]) || m[2].indexOf('/') > 0)) { acc.push(m[1], m[2]); return acc; }
            acc.push(j); return acc;
        }, []);
}
const VIDE = new Set(VIDES.map(sansAccents));

// Racine française sommaire : de quoi rapprocher diagnostic/diagnostique, artériel/artérielle, normal/normaux.
const SUFFIXES = ['issements', 'issement', 'ations', 'ation', 'ements', 'ement', 'ismes', 'isme', 'ences',
    'ence', 'ances', 'ance', 'iques', 'ique', 'euses', 'euse', 'eux', 'ives', 'ive', 'ifs', 'aux', 'ales',
    'ale', 'elles', 'elle', 'iens', 'ien', 'ions', 'ites', 'ite', 'es', 's'];
function racine(m) {
    if (m.length <= 4) return m;
    for (var i = 0; i < SUFFIXES.length; i++) {
        var s = SUFFIXES[i];
        if (m.length - s.length >= 4 && m.slice(-s.length) === s) { m = m.slice(0, -s.length); break; }
    }
    return m.length > 8 ? m.slice(0, 8) : m;
}
// Forme phonétique française approchée. « echocardiografie » et « échocardiographie » s'écrivent différemment
// mais s'entendent pareil ; c'est ainsi qu'on écrit vite et de mémoire. La distance entre les lettres, elle,
// ne rattrape ni « ph/f », ni « qu/k », ni « au/o », ni les lettres muettes de la fin.
function phonetique(m) {
    return codePhon(m).slice(0, 8);
}
function codePhon(m) {
    return m
        .replace(/ph/g, 'f').replace(/th/g, 't')
        .replace(/gu([eiy])/g, 'g$1').replace(/qu/g, 'k').replace(/q/g, 'k')
        .replace(/c([eiy])/g, 's$1').replace(/ck/g, 'k').replace(/ch/g, 'k').replace(/c/g, 'k')
        .replace(/g([eiy])/g, 'j$1')
        .replace(/x(?![aeiou])/g, 'ks').replace(/y/g, 'i').replace(/h/g, '')
        .replace(/eau|au/g, 'o').replace(/ai|ei/g, 'e').replace(/ou/g, 'u')
        .replace(/(.)\1+/g, '$1')
        .replace(/[sdtxzep]+$/, '');
}
// Distance de Levenshtein bornée à 1 : rattrape la faute de frappe, refuse les mots seulement voisins.
function presquePareil(a, b) {
    if (a === b) return true;
    if (Math.abs(a.length - b.length) > 1 || a.length < 5) return false;
    var i = 0, j = a.length - 1, k = b.length - 1;
    while (i < a.length && i < b.length && a[i] === b[i]) i++;
    while (j >= i && k >= i && a[j] === b[k]) { j--; k--; }
    return (j - i) < 1 && (k - i) < 1;
}

// ----------------------------------------------------------------- index du lexique
// Une forme peut appartenir à plusieurs groupes (EI, PC…) : on garde un ENSEMBLE de clés, et deux termes se
// correspondent dès qu'ils en partagent une.
const INDEX = new Map();
const PHON = new Map();   // même index, à l'oreille : l'orthographe approximative reste du vocabulaire connu
var MAX_NGRAMME = 1;
GROUPES.forEach(function(groupe) {
    var cle = 'c:' + sansAccents(groupe[0]).replace(/[^a-z0-9]+/g, '-');
    groupe.forEach(function(forme) {
        var mots = jetons(forme);
        if (!mots.length) return;
        if (mots.length > MAX_NGRAMME) MAX_NGRAMME = mots.length;
        var k = mots.join(' ');
        if (!INDEX.has(k)) INDEX.set(k, new Set());
        INDEX.get(k).add(cle);
        // La fiche écrit « surveillée », le lexique connaît « surveiller » : c'est la racine qui les réunit.
        if (mots.length === 1 && mots[0].length >= 6) {
            var r = racine(mots[0]);
            if (r !== mots[0]) { if (!INDEX.has(r)) INDEX.set(r, new Set()); INDEX.get(r).add(cle); }
            var ph = phonetique(mots[0]);
            if (ph.length >= 4) { if (!PHON.has(ph)) PHON.set(ph, new Set()); PHON.get(ph).add(cle); }
        }
    });
});

// ----------------------------------------------------------------- valeurs chiffrées
function uniteDe(jeton) {
    var u = String(jeton || '').replace(/[.,;]$/, '');
    return Object.prototype.hasOwnProperty.call(UNITES, u) ? u : null;
}
function nombreDe(j) {
    if (!/^\d+(?:[.,]\d+)?$/.test(j)) return null;
    return parseFloat(j.replace(',', '.'));
}
// Extrait les valeurs d'une suite de jetons. « 140/90 mmhg » en donne deux, « 11-13 sa » aussi (un intervalle).
function valeurs(mots, motsSens) {
    var out = [], sens = sensParValeur(motsSens || mots);
    for (var i = 0; i < mots.length; i++) {
        var brut = mots[i], nombres = [];
        if (/^\d+(?:[.,]\d+)?\/\d+(?:[.,]\d+)?$/.test(brut)) nombres = brut.split('/').map(nombreDe);
        else if (/^\d+(?:[.,]\d+)?-\d+(?:[.,]\d+)?$/.test(brut)) nombres = brut.split('-').map(nombreDe);
        else { var n = nombreDe(brut); if (n !== null) nombres = [n]; }
        if (!nombres.length || nombres.some(function(x) { return x === null; })) continue;
        var u = uniteDe(mots[i + 1]) || (brut.indexOf('%') >= 0 ? '%' : null);
        if (!u && /%$/.test(mots[i])) u = '%';
        nombres.forEach(function(v) { var x = normaliserValeur(v, u); x.s = sens[v] || 0; out.push(x); });
    }
    return out;
}
// À quelle valeur se rapporte quel sens : le comparateur précède le nombre (« inférieur à 8 », « < 8 »).
function sensParValeur(mots) {
    var sens = {}, courant = 0;
    for (var i = 0; i < mots.length; i++) {
        var m = mots[i];
        if (SENS_PLUS.indexOf(m) >= 0) { courant = 1; continue; }
        if (SENS_MOINS.indexOf(m) >= 0) { courant = -1; continue; }
        var n = nombreDe(m) !== null ? nombreDe(m) : (/^\d+(?:[.,]\d+)?[-\/]/.test(m) ? nombreDe(m.split(/[-\/]/)[0]) : null);
        if (n !== null) { if (courant) sens[n] = courant; courant = 0; }
    }
    return sens;
}
function normaliserValeur(v, u) {
    var def = u ? UNITES[u] : null;
    return def ? { v: v * def[1], f: def[0], u: u } : { v: v, f: '', u: '' };
}
function proche(x, y, marge) { return Math.abs(x - y) <= Math.max(0.001, Math.abs(y) * (marge || 0.02)); }
function memeValeur(a, b) {
    if (a.f && a.f === b.f) return proche(a.v, b.v);            // même famille d'unité : on compare converti
    if (!a.f || !b.f) return proche(valBrute(a), valBrute(b));  // un nombre nu vaut le nombre écrit
    return equivalenceConnue(a, b);                             // familles différentes : table d'équivalences
}
function valBrute(x) { return x.u && UNITES[x.u] ? x.v / UNITES[x.u][1] : x.v; }
function equivalenceConnue(a, b) {
    var meme = function(x, y) { return x.f === y.f && proche(x.v, y.v, 0.03); };
    return EQUIV_VALEURS.some(function(paire) {
        var p = normaliserValeur(paire[0].v, paire[0].u), q = normaliserValeur(paire[1].v, paire[1].u);
        return (meme(a, p) && meme(b, q)) || (meme(a, q) && meme(b, p));
    });
}

// ----------------------------------------------------------------- concepts d'un texte
// Passage glouton du plus long au plus court : « hypertension arterielle pulmonaire » doit gagner contre
// « hypertension arterielle », sinon HTAP serait confondu avec HTA.
// Marques de négation, et portée : jusqu'à la fin de la proposition, au plus quelques mots. « pas de fièvre »
// n'est pas « fièvre » — un sac de mots compte ces deux-là pareils, et se trompe exactement à l'envers.
const NEGATIONS = { pas: 5, ne: 5, non: 4, aucun: 4, aucune: 4, sans: 3, jamais: 4, ni: 3, absence: 4,
    exclu: 3, exclue: 3, elimine: 3, infirme: 3 };
// Négations postposées : elles portent sur ce qui PRÉCÈDE, pas sur ce qui suit.
const NEGATIONS_ARRIERE = ['normal', 'normale', 'normaux', 'normales', 'negatif', 'negative', 'negatifs',
    'negatives', 'absent', 'absente', 'absents', 'absentes', 'indemne', 'libre'];
// Sens d'une comparaison ou d'une évolution : « ≥ 140 » et « ≤ 140 » ne disent pas la même chose.
const SENS_PLUS = ['superieur', 'superieure', 'superieurs', 'plus', 'au-dela', 'augmente', 'augmentee', 'eleve',
    'elevee', 'majore', 'majoree', 'excede', 'depasse', 'hausse', 'sup'];
const SENS_MOINS = ['inferieur', 'inferieure', 'inferieurs', 'moins', 'en-deca', 'diminue', 'diminuee', 'abaisse',
    'abaissee', 'reduit', 'reduite', 'baisse', 'inf'];
export function concepts(texte) {
    var brut = sansAccents(texte), mots = jetons(texte);
    // Les signes de comparaison sont effacés par le découpage : on les récupère avant, sous forme de mots.
    var avecSens = brut.replace(/[≥>]/g, ' superieur ').replace(/[≤<]/g, ' inferieur ');
    var motsSens = jetons(avecSens);
    var cles = new Set(), nies = new Set(), racines = [], restants = [], negeReste = 0;

    // Premier passage : repérer les adjectifs de négation postposés, et marquer le mot plein qui les précède.
    var niesArriere = {};
    mots.forEach(function(m, k) {
        if (NEGATIONS_ARRIERE.indexOf(m) < 0) return;
        for (var j = k - 1; j >= 0 && j >= k - 3; j--) { if (!VIDE.has(mots[j]) && !/^\d/.test(mots[j])) { niesArriere[j] = 1; return; } }
    });
    for (var i = 0; i < mots.length;) {
        var m = mots[i];
        if (NEGATIONS_ARRIERE.indexOf(m) >= 0) { i++; continue; }
        if (Object.prototype.hasOwnProperty.call(NEGATIONS, m)) { negeReste = Math.max(negeReste, NEGATIONS[m]); i++; continue; }
        if (niesArriere[i]) negeReste = Math.max(negeReste, 1);
        var pris = 0, trouvees = null;
        for (var t = Math.min(MAX_NGRAMME, mots.length - i); t >= 1 && !pris; t--) {
            var bout = mots.slice(i, i + t).join(' ');
            if (INDEX.has(bout)) { trouvees = INDEX.get(bout); pris = t; }
        }
        // Pas de forme exacte : la racine, elle, est peut-être au lexique (« contre-indiqués » → CI).
        if (!pris && !VIDE.has(m) && m.length > 3 && INDEX.has(racine(m))) { trouvees = INDEX.get(racine(m)); pris = 1; }
        if (!pris && !VIDE.has(m) && m.length >= 5 && PHON.has(phonetique(m))) { trouvees = PHON.get(phonetique(m)); pris = 1; }
        if (pris) {
            var ou = negeReste > 0 ? nies : cles;
            trouvees.forEach(function(c) { ou.add(c); });
            i += pris; negeReste = Math.max(0, negeReste - pris); continue;
        }
        if (!VIDE.has(m) && !/^\d/.test(m) && m.length > 2 && !uniteDe(m)) {
            var r = racine(m), cleMot = 'r:' + r + '|' + (m.length >= 5 ? phonetique(m) : '');
            (negeReste > 0 ? nies : cles).add(cleMot);
            if (negeReste <= 0) racines.push({ r: r, ph: m.length >= 5 ? phonetique(m) : '' });
        } else if (/^\d/.test(m) || uniteDe(m)) restants.push(m);
        if (negeReste > 0) negeReste--;
        i++;
    }
    return { cles: cles, nies: nies, racines: racines, valeurs: valeurs(restants, motsSens), mots: mots };
}
// Une clé du point est-elle présente dans les notes ? Concept : égalité stricte. Racine : égalité, ou une
// faute de frappe près.
function cleePresente(cle, notes) {
    if (notes.cles.has(cle)) return true;
    if (cle.slice(0, 2) !== 'r:') return false;
    var coupe = cle.slice(2).split('|'), r = coupe[0], ph = coupe[1];
    return notes.racines.some(function(x) {
        if (presquePareil(r, x.r)) return true;
        if (ph && x.ph && x.ph === ph) return true;                      // même son, orthographe libre
        var court = x.r.length < r.length ? x.r : r, long = x.r.length < r.length ? r : x.r;
        return court.length >= 5 && long.indexOf(court) === 0;           // « respi » pour « respiratoire »
    });
}
// Le point est-il CONTREDIT par une négation des notes ? « pas de fièvre » quand la fiche dit « fièvre ».
function cleeNiee(cle, notes) {
    if (notes.nies.has(cle)) return true;
    if (cle.slice(0, 2) !== 'r:') return false;
    var r = cle.slice(2).split('|')[0];
    return Array.from(notes.nies).some(function(k) { return k.slice(0, 2) === 'r:' && presquePareil(r, k.slice(2).split('|')[0]); });
}

// ----------------------------------------------------------------- évaluation d'un point
// Un concept nommé du lexique pèse plus qu'un mot quelconque ; un mot présent dans presque tous les points de
// la fiche ne distingue rien et pèse moins. Sans cela, la prose de la fiche noierait le fait à retenir.
function poids(cle, frequence, nbPoints) {
    var p = cle.slice(0, 2) === 'c:' ? 2 : 1;
    if (nbPoints >= 3 && frequence / nbPoints > 0.5) p *= 0.4;
    return p;
}
// Une valeur chiffrée pèse trois mots : aux EDN, le point EST souvent le chiffre. C'est aussi ce qui permet de
// reconnaître une note télégraphique — « PA >= 140/90 x2 » ne reprend presque aucun mot de la phrase de la
// fiche, mais donne exactement ce qu'elle demande de savoir.
const POIDS_VALEUR = 3;
const SEUIL_HAUT = 0.55, SEUIL_BAS = 0.25;

export function evaluerPoints(pointsFiche, notes) {
    var refs = (pointsFiche || []).map(function(t) { return concepts(t); });
    var freq = {};
    refs.forEach(function(r) { r.cles.forEach(function(c) { freq[c] = (freq[c] || 0) + 1; }); });
    var N = concepts(notes || '');
    var phrases = decouperPhrases(notes || '').map(function(p) { return { texte: p, c: concepts(p) }; });

    return refs.map(function(ref, idx) {
        var total = 0, trouve = 0, inverse = null, vus = 0, tousConcepts = ref.cles.size + ref.nies.size;
        ref.cles.forEach(function(c) {
            var p = poids(c, freq[c] || 1, refs.length);
            total += p;
            if (cleePresente(c, N)) { trouve += p; vus++; }
            else if (cleeNiee(c, N)) { vus++; inverse = inverse || { sens: 'nie', cle: c }; }   // la fiche l'affirme, tes notes le nient
        });
        // Un point négatif de la fiche (« il n'y a pas d'indication à… ») compte comme les autres, mais il faut
        // le retrouver NIÉ dans les notes : l'affirmer, c'est dire le contraire.
        ref.nies.forEach(function(c) {
            var p = poids(c, freq[c] || 1, refs.length);
            total += p;
            if (cleeNiee(c, N)) { trouve += p; vus++; }
            else if (cleePresente(c, N)) { vus++; inverse = inverse || { sens: 'affirme', cle: c }; }
        });
        var abordes = tousConcepts ? vus / tousConcepts : 0;
        // Les valeurs comptent DANS la couverture, elles ne sont pas une barrière posée à côté. Sinon la prose
        // de la fiche (« le diagnostic repose sur… ») noie le chiffre qui est justement le point à savoir.
        var attendues = ref.valeurs;
        var manquantes = attendues.filter(function(v) { return !N.valeurs.some(function(w) { return memeValeur(v, w); }); });
        // …mais jamais au point de noyer les mots : un point bourré de chiffres resterait sinon « manque » alors
        // que l'étudiant a nommé la bonne chose sans les valeurs — ce qui est « partiel », pas « rien ».
        var pValeur = attendues.length ? Math.min(attendues.length * POIDS_VALEUR, Math.max(total, POIDS_VALEUR)) / attendues.length : 0;
        total += attendues.length * pValeur;
        trouve += (attendues.length - manquantes.length) * pValeur;
        var couv = total ? trouve / total : 0;
        var toutesLa = manquantes.length === 0;

        var etat;
        if (couv >= SEUIL_HAUT) etat = toutesLa ? 'ok' : 'partiel';
        else if (couv >= SEUIL_BAS) etat = 'partiel';
        else etat = 'manque';

        var err = etat === 'manque' ? null : contradiction(ref, phrases, attendues, manquantes);
                // Une inversion se juge sur les concepts ABORDÉS, quelle que soit leur polarité : un point
        // entièrement retourné (« pas de douleur » contre « douleur ») a une couverture nulle par
        // construction. C'est pourtant là qu'il faut parler, pas là qu'il faut se taire.
        if (!err && inverse && abordes >= 0.5) err = { inversion: inverse.sens, texte: pointsFiche[idx] };
        return { i: idx, etat: etat, couverture: Math.round(couv * 100) / 100, erreur: err };
    });
}
function decouperPhrases(t) {
    return String(t || '').split(/[.;\n•·]+|\s-\s/).map(function(s) { return s.trim(); }).filter(function(s) { return s.length > 1; });
}
// Seule erreur qu'on puisse établir sans comprendre : un nombre à la place d'un autre, dans la phrase des
// notes qui parle de ce point, avec la même unité. Tout le reste demanderait de comprendre le sens.
function contradiction(ref, phrases, attendues, manquantes) {
    if (!manquantes.length || !phrases.length) return null;
    var best = null, bestScore = 0;
    phrases.forEach(function(p) {
        var n = 0; ref.cles.forEach(function(c) { if (cleePresente(c, p.c)) n++; });
        var s = ref.cles.size ? n / ref.cles.size : 0;
        if (s > bestScore) { bestScore = s; best = p; }
    });
    if (!best || bestScore < 0.34) return null;
    for (var i = 0; i < manquantes.length; i++) {
        var attendue = manquantes[i];
        // Nombre nu (un score, un âge) : on n'accuse que si la fiche n'attend QU'UNE valeur et que la phrase
        // des notes parle vraiment de ce point. Sinon deux chiffres sans rapport suffiraient à crier à l'erreur.
        if (!attendue.f && (attendues.length !== 1 || bestScore < 0.5)) continue;
        var rivale = best.c.valeurs.find(function(w) {
            return (attendue.f ? w.f === attendue.f : !w.f) && !memeValeur(attendue, w);
        });
        if (rivale) return { ecrit: ecrire(rivale), correct: ecrire(attendue), phrase: best.texte };
    }
    return null;
}
function ecrire(x) {
    var v = valBrute(x);
    return (Math.round(v * 1000) / 1000).toString().replace('.', ',') + (x.u ? (x.u === '%' ? ' %' : ' ' + x.u) : '');
}

// ----------------------------------------------------------------- note d'une section
// Même arithmétique que la correction IA (ednNoteSection dans api.php) : la part des points restitués, un
// point à moitié valant une demi-part, moins un malus par erreur avec un plancher. Les deux corrections
// doivent se lire sur la même échelle, sinon les comparer n'a pas de sens.
export function noteSection(etats, nbErreurs) {
    if (!etats || !etats.length) return null;
    var acquis = etats.reduce(function(a, e) { return a + (e === 'ok' ? 1 : (e === 'partiel' ? 0.5 : 0)); }, 0);
    var note = acquis / etats.length;
    if (nbErreurs > 0) note = Math.max(0.2, note - 0.15 * nbErreurs);
    return Math.round(Math.max(0, Math.min(1, note)) * 1000) / 1000;
}

// ----------------------------------------------------------------- sortie au format de la correction IA
// Même forme que les « points » renvoyés par l'action ai_recall_analyse : l'écran de bilan, le journal
// d'apprentissage et les lacunes n'ont rien à savoir de qui a corrigé.
export function corrigerRecall(points) {
    return (points || []).map(function(pt) {
        var fiche = (pt.items && pt.items.length) ? pt.items : [];
        var res = evaluerPoints(fiche, pt.notes || '');
        var etats = res.map(function(r) { return r.etat; });
        var erreurs = res.filter(function(r) { return r.erreur; }).map(function(r) {
            var e = r.erreur;
            if (e.inversion) return {
                ecrit: e.inversion === 'nie' ? 'tu l\'as noté comme absent ou nié' : 'tu l\'as noté comme présent',
                correct: court(e.texte),
                pourquoi: e.inversion === 'nie'
                    ? 'La fiche l\'affirme, tes notes le nient. Une affirmation retournée se réapprend telle quelle : relis ce point.'
                    : 'La fiche le nie, tes notes l\'affirment. Une affirmation retournée se réapprend telle quelle : relis ce point.',
            };
            return { ecrit: e.ecrit, correct: e.correct,
                pourquoi: 'Ta note donne ' + e.ecrit + ' là où la fiche dit ' + e.correct + '. Vérifie ce chiffre : c\'est le genre de valeur qui tombe telle quelle aux EDN.' };
        });
        var note = noteSection(etats, erreurs.length);
        return {
            id: pt.id, score: note == null ? 0 : note, items: etats, erreurs: erreurs,
            acquis: fiche.filter(function(_, k) { return etats[k] === 'ok'; }).map(court).slice(0, 4),
            manques: fiche.filter(function(_, k) { return etats[k] !== 'ok'; }).map(court).slice(0, 5),
            notions: [], source: 'local',
        };
    });
}
function court(t) { t = String(t || '').trim(); return t.length > 90 ? t.slice(0, 87) + '…' : t; }

// Le code historique lit encore ces noms sur window.
if (typeof window !== 'undefined') {
    window.corrigerRecallLocal = corrigerRecall;
    window.recallConcepts = concepts;
    window.recallNoteSection = noteSection;
}
